import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ApiService } from 'src/app/services/api.service';
import { ToastMessageService } from 'src/app/widgets/toast-message/toast-message.service';
import { DEFAULT_COUNTRY_CALLING_CODE } from 'src/app/constants/country-calling-codes';

@Component({
  selector: 'app-send-apply-financing-text-dialog',
  templateUrl: './send-apply-financing-text-dialog.component.html',
  styleUrls: ['./send-apply-financing-text-dialog.component.scss']
})
export class SendApplyFinancingTextDialogComponent {

  isSubmitted = false;
  formProcessing = false;

  // messages the api sends back, e.g. the store has no finance connect link
  formErrors: any = null;

  form: UntypedFormGroup;

  private _sendTextEp = `${this._apiSvc.endpoints.sales}/api/SoHead/SendApplyFinancingText`;

  constructor(
    private _apiSvc: ApiService,
    private _fb: UntypedFormBuilder,
    private _toastMsgSvc: ToastMessageService,
    public dialogRef: MatDialogRef<SendApplyFinancingTextDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = this._fb.group({
      // only +1 numbers are supported, so the code is shown but not editable
      CountryCode: [{ value: DEFAULT_COUNTRY_CALLING_CODE, disabled: true }],
      PhoneNumber: [null, [Validators.required, SendApplyFinancingTextDialogComponent.phoneNumberValidator]],
      SmsConsent: [false, Validators.requiredTrue]
    });
  }

  /**
   * The country code is picked from its own field, so validate the digits the
   * user typed and allow the separators they are used to
   * @param ctrl
   */
  static phoneNumberValidator(ctrl: AbstractControl): ValidationErrors | null {

    const value = String(ctrl.value ?? '');

    // required handles an empty field
    if (!value.trim()) {
      return null;
    }

    // anything other than digits and the usual separators is not a phone number
    if (/[^0-9\s\-\.\(\)]/.test(value)) {
      return { phoneNumber: true };
    }

    const digits = value.replace(/\D/g, '');

    return digits.length >= 6 && digits.length <= 14 ? null : { phoneNumber: true };
  }

  /**
   * The selected country code and the digits entered, combined into the E.164
   * number the text will be sent to
   */
  get fullPhoneNumber(): string {

    const digits = String(this.form.value.PhoneNumber ?? '').replace(/\D/g, '');

    if (!digits) {
      return '';
    }

    // read the control directly - disabled controls are left out of form.value
    return `${this.form.controls.CountryCode.value}${digits}`;
  }

  onClose(): void {
    this.dialogRef.close({ confirm: false });
  }

  onSubmit(e: any): void {
    e.preventDefault();
    this.isSubmitted = true;

    if (!this.form.valid || this.formProcessing) {
      return;
    }

    // clear errors
    this.formErrors = null;

    this.formProcessing = true;

    const payload = {
      ToPhoneNumber: this.fullPhoneNumber,
      StoreRefId: this.data.storeRefId,
      Consented: this.form.value.SmsConsent
    };

    this._apiSvc.add(this._sendTextEp, payload)
      .subscribe(() => {

        this._toastMsgSvc.send({
          type: 'success',
          message: 'Financing text sent',
          icon: 'check_circle_outline'
        });

        this.formProcessing = false;
        this.dialogRef.close({ confirm: true });

      }, (err: any) => {

        // the api explains why it refused - a store with no finance connect
        // link set up, for one - so show that instead of a generic failure
        const apiErrors = err?.error?.Errors;

        this.formErrors = apiErrors?.length ? apiErrors : [{ Message: 'Failed to send financing text' }];

        this.formProcessing = false;

      });
  }

}
